// The calendar's pictures — one place that says which branded image stands
// for which state, so a badge means the same thing in every row, list and tile.
// The images live in the shared package (packages/ui/src/branding).
//
// Interface glyphs (back, zoom, arrows) stay Phosphor; these are only for
// people, places and states.

import { cn } from "@bednbody/ui/lib/utils";
import riderAvatarUrl from "@bednbody/ui/branding/avatars/rider.png";
import therapistAvatarUrl from "@bednbody/ui/branding/avatars/therapist.png";
import riderTopUrl from "@bednbody/ui/branding/map/rider-top.png";
import homeUrl from "@bednbody/ui/branding/places/home.png";
import hubUrl from "@bednbody/ui/branding/places/hub.png";
import dayOffUrl from "@bednbody/ui/branding/status/day-off.png";
import disconnectedUrl from "@bednbody/ui/branding/status/disconnected.png";
import freeUrl from "@bednbody/ui/branding/status/free.png";
import inSessionUrl from "@bednbody/ui/branding/status/in-session.png";
import needsTherapistUrl from "@bednbody/ui/branding/status/needs-therapist.png";
import notClockedInUrl from "@bednbody/ui/branding/status/not-clocked-in.png";
import onBreakUrl from "@bednbody/ui/branding/status/on-break.png";
import sosUrl from "@bednbody/ui/branding/status/sos.png";
import waitingPickupUrl from "@bednbody/ui/branding/status/waiting-pickup.png";
import type { CalendarDispatchLegKind } from "@bednbody/types";
import type { PersonStatus } from "./calendar-board";

export const BADGE = {
  dayOff: dayOffUrl,
  disconnected: disconnectedUrl,
  free: freeUrl,
  inSession: inSessionUrl,
  needsTherapist: needsTherapistUrl,
  notClockedIn: notClockedInUrl,
  onBreak: onBreakUrl,
  sos: sosUrl,
  waitingPickup: waitingPickupUrl,
} as const;

/** `null` where a state has no badge of its own (a plain plan on another day). */
const STATUS_BADGE: Record<Exclude<PersonStatus["kind"], "on_trip">, string | null> = {
  day_off: dayOffUrl,
  scheduled: null,
  not_clocked_in: notClockedInUrl,
  in_session: inSessionUrl,
  free: freeUrl,
  sos: sosUrl,
  disconnected: disconnectedUrl,
  on_break: onBreakUrl,
  waiting_pickup: waitingPickupUrl,
};

const SIZE = {
  xs: "size-4",
  sm: "size-5",
  md: "size-7",
  lg: "size-9",
} as const;

type Size = keyof typeof SIZE;

/**
 * A state's round badge. "On a trip" has no file: it is the map's own
 * top-down rider in a blue disc, so the badge and the map pin are one picture.
 */
export function StatusBadge({
  status,
  size = "sm",
  className,
}: {
  status: PersonStatus;
  size?: Size;
  className?: string;
}) {
  if (status.kind === "on_trip") {
    return (
      <span
        aria-hidden="true"
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full bg-info ring-2 ring-card",
          SIZE[size],
          className,
        )}
      >
        <img alt="" className="size-[70%] object-contain" src={riderTopUrl} />
      </span>
    );
  }
  const url = STATUS_BADGE[status.kind];
  return url ? <BadgeImage className={className} size={size} src={url} /> : null;
}

export function BadgeImage({
  src,
  size = "sm",
  className,
}: {
  src: string;
  size?: Size;
  className?: string;
}) {
  return (
    <img
      alt=""
      aria-hidden="true"
      className={cn("shrink-0 object-contain", SIZE[size], className)}
      src={src}
    />
  );
}

/** The role's face — what shows until a real staff photo exists (0279). */
export function avatarUrl(role: "therapist" | "rider"): string {
  return role === "rider" ? riderAvatarUrl : therapistAvatarUrl;
}

/** A person: their face (or the role's), with their state badge on the corner. */
export function StaffAvatar({
  role,
  photo,
  status,
  size = "md",
}: {
  role: "therapist" | "rider";
  photo?: string | null;
  status?: PersonStatus;
  size?: "md" | "lg";
}) {
  return (
    <span className={cn("relative shrink-0", size === "lg" ? "size-12" : "size-9")}>
      <img
        alt=""
        aria-hidden="true"
        className="size-full rounded-full bg-muted object-cover ring-1 ring-border"
        src={photo ?? avatarUrl(role)}
      />
      {status ? (
        <StatusBadge
          className="absolute -bottom-1 -right-1 rounded-full ring-2 ring-card"
          size={size === "lg" ? "sm" : "xs"}
          status={status}
        />
      ) : null}
    </span>
  );
}

/** Where a rider leg ends: the client's home, or back at the hub. */
export function legPlaceUrl(kind: CalendarDispatchLegKind): string {
  return kind === "return" ? hubUrl : homeUrl;
}

export { hubUrl, homeUrl };
